import { type ReactNode } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Eye,
  FileText,
  MessageSquare,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────────
 * FollowUpPanel — unified follow-up UI used by both the assessor (review)
 * and the system owner (execution) pages, for all three control types
 * (Declarative, Declarative+Evidence, Automation).
 *
 * Visual model: collapsed strip by default → click toggle to expand.
 * Expanded body lays out the same fixed sections in the same order, so
 * D / D+E / Automation all read identically.
 * ────────────────────────────────────────────────────────────────────────── */

export type FollowUpRole = "assessor" | "owner";
export type FollowUpStatus = "pending" | "resolved";
export type FollowUpControlType = "declarative" | "declarative_evidence" | "automation";

export interface FollowUpEvidence {
  id: string;
  name: string;
  /** Optional handlers. If omitted, the corresponding button is hidden. */
  onPreview?: () => void;
  onDownload?: () => void;
}

export interface FollowUpPanelProps {
  /** Which side's UI we're rendering. */
  role: FollowUpRole;
  /** Current state of the follow-up round. */
  status: FollowUpStatus;
  /** Control type — only affects which sections are shown. */
  controlType?: FollowUpControlType;

  /** Header pills. */
  controlId?: string;
  statementLabel?: string;

  /** Assessor's follow-up question. */
  assessorQuestion?: string | null;
  /** System owner's response text (justification). */
  ownerResponse?: string | null;
  /** Attached evidence list. */
  evidence?: FollowUpEvidence[];

  /** Optional chat button — number badge + open handler. */
  chatCount?: number;
  onOpenChat?: () => void;

  /** Bottom action row (Submit / Resolve / Re-raise etc). Use the
   *  exported <FollowUpAction /> button for the consistent styling. */
  actions?: ReactNode;

  /** Render an inline body slot ABOVE the response section (used by the
   *  system owner respond form to render the textarea + evidence picker). */
  responseSlot?: ReactNode;

  /** Force expand initially. Defaults to collapsed. */
  defaultExpanded?: boolean;
  /** Hide collapse toggle (always expanded). */
  alwaysExpanded?: boolean;
  /** Optional X close — only shown when supplied. */
  onClose?: () => void;
}

/* Status pill — same component for both roles. */
function StatusPill({ status }: { status: FollowUpStatus }) {
  if (status === "resolved") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
        <CheckCircle2 className="size-3" /> Follow-Up Resolved
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
      <AlertCircle className="size-3" /> Follow-Up Requested
    </span>
  );
}

/* Section caption — uniform style for all labels inside the panel. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px] font-semibold tracking-wide text-amber-700/80 dark:text-amber-400/80 mb-1.5">
      {children}
    </p>
  );
}

/* Inner read-only text block — same chrome everywhere. */
function ReadOnlyBlock({ children }: { children: ReactNode }) {
  return (
    <div className="p-3 rounded-lg bg-white/60 dark:bg-white/[0.03] border border-amber-200/70 dark:border-amber-500/20">
      <p className="text-[13px] text-amber-900 dark:text-amber-200 leading-relaxed whitespace-pre-wrap">
        {children}
      </p>
    </div>
  );
}

/* Evidence row — same chrome everywhere. */
function EvidenceRow({ index, ev }: { index: number; ev: FollowUpEvidence }) {
  return (
    <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-white/60 dark:bg-white/[0.03] border border-amber-200/70 dark:border-amber-500/20">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="flex-shrink-0 size-5 rounded-full bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center text-[10px] font-bold text-amber-700 dark:text-amber-400">
          {index + 1}
        </span>
        <FileText className="size-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <p
          className="text-[12px] font-medium text-[#0f172a] dark:text-white/80 truncate"
          title={ev.name}
        >
          {ev.name}
        </p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {ev.onPreview && (
          <button
            onClick={ev.onPreview}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors"
          >
            <Eye className="size-3" /> Preview
          </button>
        )}
        {ev.onDownload && (
          <button
            onClick={ev.onDownload}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors"
          >
            <Download className="size-3" /> Download
          </button>
        )}
      </div>
    </div>
  );
}

export function FollowUpPanel({
  role,
  status,
  controlType,
  controlId,
  statementLabel,
  assessorQuestion,
  ownerResponse,
  evidence,
  chatCount,
  onOpenChat,
  actions,
  responseSlot,
  defaultExpanded = false,
  alwaysExpanded = false,
  onClose,
}: FollowUpPanelProps) {
  const isExpanded = true;

  // Show the evidence section only when there's actually evidence to show
  // (or when an editable response slot needs the picker). Skip the
  // "No evidence attached" empty state — it's just visual noise.
  const showEvidenceSection =
    controlType !== "declarative" && (evidence?.length ?? 0) > 0;

  // Compact ownerResponse: skip decision sentinels that the assessor system
  // sometimes writes into the response field ("accepted", "rejected" etc).
  const sentinels = new Set(["accepted", "rejected", "approved"]);
  const cleanOwnerResponse =
    ownerResponse && !sentinels.has(ownerResponse.trim().toLowerCase()) ? ownerResponse : null;

  const showOwnerResponseSection =
    responseSlot == null && (status === "resolved" || !!cleanOwnerResponse);

  return (
    <div className="rounded-xl bg-amber-50 dark:bg-amber-500/[0.06] border border-amber-200 dark:border-amber-500/25 overflow-hidden">
      {/* ── Collapsed strip / header ── */}
      <div className="flex items-center gap-3 px-4 py-3">
        {controlId && (
          <span className="text-[11px] font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2 py-0.5 rounded ">
            {controlId?.toUpperCase()}
          </span>
        )}
        {statementLabel && (
          <span className="text-[12px] font-medium text-[#0f172a] dark:text-white/90 truncate">
            {statementLabel}
          </span>
        )}
        {/* Status pill is only shown on the system-owner (execution) side —
            for the owner, "Follow-Up Resolved" / "Follow-Up Requested"
            tells them whether they still owe a response. On the review
            (assessor) side it's redundant: the assessor already sees the
            review-state badge in the row + the inline action buttons. */}
        {role === "owner" && <StatusPill status={status} />}

        <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          {onOpenChat && (
            <button
              onClick={onOpenChat}
              title="View follow-up conversation"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold text-amber-700 dark:text-amber-300 bg-white/60 dark:bg-white/[0.04] border border-amber-200/70 dark:border-amber-500/20 hover:bg-amber-100/70 dark:hover:bg-amber-500/10 transition-colors"
            >
              <MessageSquare className="size-3" />
              Chat
              {!!chatCount && chatCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-amber-600 text-white text-[9px] font-bold leading-none">
                  {chatCount}
                </span>
              )}
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              title="Close"
              className="size-7 rounded-md flex items-center justify-center text-amber-700 dark:text-amber-300 hover:bg-amber-100/70 dark:hover:bg-amber-500/10 transition-colors"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* ── Expanded body ── */}
      {isExpanded && (
        <div className="border-t border-amber-200/70 dark:border-amber-500/20 px-4 py-4 space-y-3">
          {/* Assessor question */}
          {assessorQuestion && (
            <div>
              <SectionLabel>
                {role === "assessor" ? "Assessor Question (You)" : "Assessor Asked"}
              </SectionLabel>
              <ReadOnlyBlock>{assessorQuestion}</ReadOnlyBlock>
            </div>
          )}

          {/* System owner response — either editable slot or read-only text */}
          {responseSlot != null ? (
            <div>
              <SectionLabel>Your Response</SectionLabel>
              {responseSlot}
            </div>
          ) : (
            showOwnerResponseSection && (
              <div>
                <SectionLabel>
                  {role === "owner" ? "Your Response" : "System Owner Response"}
                </SectionLabel>
                <ReadOnlyBlock>
                  {cleanOwnerResponse?.trim() || (
                    <span className="text-amber-700/70 dark:text-amber-400/60 italic">
                      No response submitted yet.
                    </span>
                  )}
                </ReadOnlyBlock>
              </div>
            )
          )}

          {/* Evidence — only rendered when files are actually attached;
              empty state is intentionally omitted. */}
          {showEvidenceSection && evidence && evidence.length > 0 && (
            <div>
              <SectionLabel>Attached Evidence ({evidence.length})</SectionLabel>
              <div className="space-y-1.5">
                {evidence.map((ev, i) => (
                  <EvidenceRow key={ev.id} index={i} ev={ev} />
                ))}
              </div>
            </div>
          )}

          {/* Action row (always bottom-right) */}
          {actions && (
            <div className="flex items-center justify-end gap-2 pt-1">{actions}</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * <FollowUpAction /> — primary/secondary button variants matched to the
 * panel's color tokens. Use these inside the `actions` slot so every panel
 * has the same button look.
 * ────────────────────────────────────────────────────────────────────────── */
export interface FollowUpActionProps {
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "danger";
  type?: "button" | "submit";
  children: ReactNode;
  title?: string;
}

export function FollowUpAction({
  onClick,
  disabled,
  variant = "secondary",
  type = "button",
  children,
  title,
}: FollowUpActionProps) {
  const styles: Record<NonNullable<FollowUpActionProps["variant"]>, string> = {
    primary:
      "bg-amber-600 text-white hover:bg-amber-700 disabled:bg-[#e2e8f0] disabled:text-[#94a3b8] disabled:cursor-not-allowed",
    secondary:
      "bg-white/70 dark:bg-white/[0.04] text-amber-700 dark:text-amber-300 border border-amber-200/70 dark:border-amber-500/20 hover:bg-amber-100/70 dark:hover:bg-amber-500/10",
    danger:
      "bg-red-600 text-white hover:bg-red-700 disabled:bg-[#e2e8f0] disabled:text-[#94a3b8] disabled:cursor-not-allowed",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-bold transition-all ${styles[variant]}`}
    >
      {children}
    </button>
  );
}
